import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';

const AIModelPreferenceContext = createContext({});

export const useAIModelPreference = () => {
  const context = useContext(AIModelPreferenceContext);
  if (!context) {
    throw new Error('useAIModelPreference must be used within an AIModelPreferenceProvider');
  }
  return context;
};

export const AIModelPreferenceProvider = ({ children }) => {
  const { user } = useAuth();
  const [selectedModel, setSelectedModel] = useState('anthropic');
  const [loading, setLoading] = useState(true);

  // Load preference from localStorage or Supabase on mount
  useEffect(() => {
    const loadPreference = async () => {
      try {
        // First check localStorage for immediate UI update
        const localPreference = localStorage.getItem('ai_model_preference');
        if (localPreference) {
          setSelectedModel(localPreference);
        }

        // If user is logged in, fetch from Supabase
        if (user?.id) {
          const { data, error } = await supabase?.from('user_profiles')?.select('ai_model_preference')?.eq('user_id', user?.id)?.single();

          if (!error && data?.ai_model_preference) {
            setSelectedModel(data?.ai_model_preference);
            localStorage.setItem('ai_model_preference', data?.ai_model_preference);
          }
        }
      } catch (error) {
        console.error('Error loading AI model preference:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPreference();
  }, [user?.id]);

  // Update preference in both localStorage and Supabase
  const updateModelPreference = async (model) => {
    try {
      setSelectedModel(model);
      localStorage.setItem('ai_model_preference', model);

      // If user is logged in, save to Supabase
      if (user?.id) {
        const { error } = await supabase?.from('user_profiles')?.update({ ai_model_preference: model })?.eq('user_id', user?.id);

        if (error) {
          console.error('Error saving AI model preference:', error);
        }
      }
    } catch (error) {
      console.error('Error updating AI model preference:', error);
    }
  };

  const value = {
    selectedModel,
    setSelectedModel: updateModelPreference,
    loading
  };

  return (
    <AIModelPreferenceContext.Provider value={value}>
      {children}
    </AIModelPreferenceContext.Provider>
  );
};
